import React, { useState, useEffect } from 'react';
import adminApi from '../utils/adminApi';
import '../styles/AdminLEDControl.css';

const AdminLEDControl = () => {
  const [connectedPanels, setConnectedPanels] = useState({
    ambient: 0,
    music: 0
  });
  const [message, setMessage] = useState({ text: '', type: '' });

  // Music sequence settings
  const [musicSettings, setMusicSettings] = useState({
    selectedSequence: 'countdown',
    duration: 10000,
    bpm: 128,
    intensity: 1.0,
    color: '#FF0000',
    color2: '#0000FF'
  });

  // Ambient test settings
  const [ambientTest, setAmbientTest] = useState({
    eventType: 'battleship:hit',
    team: 'Red Team'
  });

  // Fetch connected panel count
  useEffect(() => {
    const fetchPanelStatus = async () => {
      try {
        const response = await adminApi.get('/api/admin/led/status');
        if (response.data.success) {
          setConnectedPanels(response.data.panels);
        }
      } catch (error) {
        console.error('Error fetching panel status:', error);
      }
    };

    fetchPanelStatus();
    const interval = setInterval(fetchPanelStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  // Trigger music sequence
  const triggerMusicSequence = async (command, customOptions = {}) => {
    try {
      const options = command === 'custom' ? {
        command: musicSettings.selectedSequence,
        duration: musicSettings.duration,
        bpm: musicSettings.bpm,
        intensity: musicSettings.intensity,
        color: musicSettings.color,
        color2: musicSettings.color2,
        ...customOptions
      } : {
        command,
        ...customOptions
      };

      const response = await adminApi.post('/api/admin/music/trigger', options);

      if (response.data.success) {
        setMessage({
          text: `✓ Triggered "${command}" on ${response.data.panelsNotified} panel(s)`,
          type: 'success'
        });
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      }
    } catch (error) {
      console.error('Error triggering music sequence:', error);
      setMessage({
        text: `✗ Error: ${error.response?.data?.error || error.message}`,
        type: 'error'
      });
    }
  };

  // Trigger ambient effect test
  const triggerAmbientEffect = async () => {
    try {
      const response = await adminApi.post('/api/admin/ambient/trigger', {
        eventType: ambientTest.eventType,
        team: ambientTest.team,
        x: 10,
        y: 15
      });

      if (response.data.success) {
        setMessage({
          text: `✓ Triggered "${ambientTest.eventType}" on ${response.data.panelsNotified} panel(s)`,
          type: 'success'
        });
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      }
    } catch (error) {
      console.error('Error triggering ambient effect:', error);
      setMessage({
        text: `✗ Error: ${error.response?.data?.error || error.message}`,
        type: 'error'
      });
    }
  };

  return (
    <div className="led-control-container">
      <h2>💡 LED Panel Control</h2>

      {message.text && (
        <div className={`led-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Panel Status */}
      <div className="panel-status-section">
        <h3>📊 Connected Panels</h3>
        <div className="panel-status-grid">
          <div className="panel-status-card">
            <div className="panel-count">{connectedPanels.ambient}</div>
            <div className="panel-label">Ambient Panels</div>
            <div className="panel-description">React to game events</div>
          </div>
          <div className="panel-status-card">
            <div className="panel-count">{connectedPanels.music}</div>
            <div className="panel-label">Music Panels</div>
            <div className="panel-description">Synchronized sequences</div>
          </div>
        </div>
      </div>

      {/* Music Sequences */}
      <div className="music-sequences-section">
        <h3>🎵 Music Sequences</h3>

        <div className="quick-sequences">
          <h4>Quick Sequences</h4>
          <div className="sequence-grid">
            <button
              className="sequence-btn countdown"
              onClick={() => triggerMusicSequence('countdown')}
              title="10-second countdown with numbers"
            >
              🔢 Countdown
            </button>

            <button
              className="sequence-btn market-open"
              onClick={() => triggerMusicSequence('market_open')}
              title="Epic market opening sequence"
            >
              📈 Market Open
            </button>

            <button
              className="sequence-btn market-close"
              onClick={() => triggerMusicSequence('market_close')}
              title="Market closing fade"
            >
              📉 Market Close
            </button>

            <button
              className="sequence-btn victory"
              onClick={() => triggerMusicSequence('victory')}
              title="Victory celebration"
            >
              🎉 Victory
            </button>

            <button
              className="sequence-btn strobe"
              onClick={() => triggerMusicSequence('strobe', { duration: 5000 })}
              title="Fast white strobe"
            >
              ⚡ Strobe (5s)
            </button>

            <button
              className="sequence-btn rainbow"
              onClick={() => triggerMusicSequence('rainbow_wave', { duration: 10000 })}
              title="Rainbow wave effect"
            >
              🌈 Rainbow Wave
            </button>

            <button
              className="sequence-btn buildup"
              onClick={() => triggerMusicSequence('buildup', { duration: 8000 })}
              title="Build-up effect (8s)"
            >
              📊 Build-up
            </button>

            <button
              className="sequence-btn drop"
              onClick={() => triggerMusicSequence('drop', { duration: 3000 })}
              title="Intense drop effect"
            >
              💥 Drop
            </button>

            <button
              className="sequence-btn stop"
              onClick={() => triggerMusicSequence('stop')}
              title="Stop all sequences"
            >
              ⏹️ STOP ALL
            </button>
          </div>
        </div>

        <div className="custom-sequence">
          <h4>Custom Sequence</h4>

          <div className="custom-controls">
            <div className="control-group">
              <label>Sequence Type:</label>
              <select
                value={musicSettings.selectedSequence}
                onChange={(e) => setMusicSettings({ ...musicSettings, selectedSequence: e.target.value })}
              >
                <option value="countdown">Countdown</option>
                <option value="market_open">Market Open</option>
                <option value="market_close">Market Close</option>
                <option value="beat_pulse">Beat Pulse</option>
                <option value="strobe">Strobe</option>
                <option value="rainbow_wave">Rainbow Wave</option>
                <option value="color_wash">Color Wash</option>
                <option value="buildup">Build-up</option>
                <option value="drop">Drop</option>
                <option value="victory">Victory</option>
              </select>
            </div>

            <div className="control-group">
              <label>Duration (ms):</label>
              <input
                type="number"
                value={musicSettings.duration}
                onChange={(e) => setMusicSettings({ ...musicSettings, duration: parseInt(e.target.value) })}
                min="1000"
                max="60000"
                step="1000"
              />
            </div>

            <div className="control-group">
              <label>BPM (for beat_pulse):</label>
              <input
                type="number"
                value={musicSettings.bpm}
                onChange={(e) => setMusicSettings({ ...musicSettings, bpm: parseInt(e.target.value) })}
                min="60"
                max="200"
              />
            </div>

            <div className="control-group">
              <label>Intensity (0-1):</label>
              <input
                type="range"
                value={musicSettings.intensity}
                onChange={(e) => setMusicSettings({ ...musicSettings, intensity: parseFloat(e.target.value) })}
                min="0"
                max="1"
                step="0.1"
              />
              <span>{musicSettings.intensity.toFixed(1)}</span>
            </div>

            <div className="control-group">
              <label>Primary Color:</label>
              <input
                type="color"
                value={musicSettings.color}
                onChange={(e) => setMusicSettings({ ...musicSettings, color: e.target.value })}
              />
            </div>

            <div className="control-group">
              <label>Secondary Color:</label>
              <input
                type="color"
                value={musicSettings.color2}
                onChange={(e) => setMusicSettings({ ...musicSettings, color2: e.target.value })}
              />
            </div>

            <button
              className="trigger-custom-btn"
              onClick={() => triggerMusicSequence('custom')}
            >
              🚀 Trigger Custom Sequence
            </button>
          </div>
        </div>
      </div>

      {/* Ambient Effects Testing */}
      <div className="ambient-test-section">
        <h3>🎮 Ambient Effect Testing</h3>
        <p className="section-description">
          Test ambient panels with simulated game events
        </p>

        <div className="ambient-controls">
          <div className="control-group">
            <label>Event Type:</label>
            <select
              value={ambientTest.eventType}
              onChange={(e) => setAmbientTest({ ...ambientTest, eventType: e.target.value })}
            >
              <optgroup label="Portfolio Game Events">
                <option value="battleship:hit">Portfolio Hit</option>
                <option value="battleship:miss">Portfolio Miss</option>
                <option value="battleship:sunk">Portfolio Ship Sunk</option>
              </optgroup>
              <optgroup label="Market Events">
                <option value="market:open">Market Open</option>
                <option value="market:close">Market Close</option>
                <option value="market:halt">Market Halt</option>
              </optgroup>
              <optgroup label="Trading Events">
                <option value="trade:large">Large Trade</option>
              </optgroup>
              <optgroup label="Team Events">
                <option value="team:milestone">Team Milestone</option>
              </optgroup>
            </select>
          </div>

          <div className="control-group">
            <label>Team:</label>
            <select
              value={ambientTest.team}
              onChange={(e) => setAmbientTest({ ...ambientTest, team: e.target.value })}
            >
              <option value="Red Team">Red Team</option>
              <option value="Blue Team">Blue Team</option>
              <option value="Green Team">Green Team</option>
              <option value="Yellow Team">Yellow Team</option>
            </select>
          </div>

          <button
            className="trigger-ambient-btn"
            onClick={triggerAmbientEffect}
          >
            🧪 Test Ambient Effect
          </button>
        </div>
      </div>

      {/* Automated Workflows */}
      <div className="automated-workflows-section">
        <h3>⚙️ Automated Workflows</h3>
        <p className="section-description">
          Pre-programmed sequences for common scenarios
        </p>

        <div className="workflow-grid">
          <button
            className="workflow-btn"
            onClick={async () => {
              setMessage({ text: 'Starting market open workflow...', type: 'info' });
              await new Promise(resolve => setTimeout(resolve, 5000));
              await triggerMusicSequence('countdown');
              await new Promise(resolve => setTimeout(resolve, 10000));
              await triggerMusicSequence('market_open');
              setMessage({ text: '✓ Market open workflow complete!', type: 'success' });
            }}
          >
            <div className="workflow-icon">📈</div>
            <div className="workflow-title">Market Opening</div>
            <div className="workflow-description">
              5s wait → 10s countdown → Epic opening
            </div>
          </button>

          <button
            className="workflow-btn"
            onClick={async () => {
              setMessage({ text: 'Starting music drop workflow...', type: 'info' });
              await triggerMusicSequence('buildup', { duration: 8000 });
              await new Promise(resolve => setTimeout(resolve, 8000));
              await triggerMusicSequence('drop', { duration: 3000 });
              setMessage({ text: '✓ Music drop complete!', type: 'success' });
            }}
          >
            <div className="workflow-icon">🎵</div>
            <div className="workflow-title">Music Drop</div>
            <div className="workflow-description">
              8s build-up → 3s intense drop
            </div>
          </button>

          <button
            className="workflow-btn"
            onClick={async () => {
              await triggerAmbientEffect();
              await triggerMusicSequence('victory', { duration: 10000 });
            }}
          >
            <div className="workflow-icon">🏆</div>
            <div className="workflow-title">Team Victory</div>
            <div className="workflow-description">
              Ambient + Music celebration
            </div>
          </button>
        </div>
      </div>

      {/* Help Section */}
      <div className="led-help-section">
        <h4>ℹ️ Quick Help</h4>
        <ul>
          <li><strong>Ambient Panels:</strong> Automatically react to game events (hits, trades, etc.)</li>
          <li><strong>Music Panels:</strong> Trigger synchronized light sequences manually</li>
          <li><strong>Workflows:</strong> Automate common sequences (market open, drops, etc.)</li>
          <li><strong>Stop All:</strong> Emergency stop button stops all running sequences</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminLEDControl;
